import React, { useMemo, useCallback, forwardRef, MutableRefObject, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import useClickOutside from 'src/lib/hooks/useClickOutside'

export type SheetDirection = 'left' | 'right' | 'top' | 'bottom'

const BACKDROP_FADE_EASE = [0, 0, 0.2, 1] as const
/** Match ~spring length so overlay doesn’t disappear before the sheet settles. */
const BACKDROP_FADE_DURATION = 0.45

/** Clicks inside these (often portaled) should not count as “outside” the sheet. */
const OUTSIDE_CLICK_IGNORE_SELECTORS = [
  '[role="dialog"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[data-radix-popper-content-wrapper]'
] as const

export function shouldIgnoreOutsideClickTarget(el: Element | null): boolean {
  if (!el) return false
  return OUTSIDE_CLICK_IGNORE_SELECTORS.some((sel) => el.closest(sel))
}

export interface GenericSlideInDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  children: React.ReactNode
  direction?: SheetDirection
  width?: number | string
  height?: number | string
  /** Legacy sizing: percentage of viewport along the slide axis. Ignored when `width`/`height` is set. */
  coverage?: number
  hideOverlay?: boolean
  disableOutsideClick?: boolean
  /** When true, backdrop and sheet use absolute positioning in the nearest positioned ancestor (e.g. main column). */
  withinContainer?: boolean
  className?: string
  /** Outside mousedown (outside sheet panel) invokes this with the native event. */
  onBackdropClick?: (event: MouseEvent) => void
  /** Fires once the enter spring settles at rest (x: 0, y: 0). Not called for exit. */
  onEnterComplete?: () => void
}

function slideOffsets(direction: SheetDirection): { initial: Record<string, string | number>; exit: Record<string, string | number> } {
  switch (direction) {
    case 'right':
      return { initial: { x: '100%', y: 0 }, exit: { x: '100%', y: 0 } }
    case 'left':
      return { initial: { x: '-100%', y: 0 }, exit: { x: '-100%', y: 0 } }
    case 'bottom':
      return { initial: { x: 0, y: '100%' }, exit: { x: 0, y: '100%' } }
    case 'top':
      return { initial: { x: 0, y: '-100%' }, exit: { x: 0, y: '-100%' } }
  }
}

const GenericSlideInDialog = forwardRef<HTMLDivElement, GenericSlideInDialogProps>(
  (
    {
      isOpen,
      setIsOpen,
      children,
      direction = 'bottom',
      width,
      height,
      coverage,
      hideOverlay = false,
      disableOutsideClick = false,
      withinContainer = false,
      className = '',
      onBackdropClick,
      onEnterComplete
    },
    forwardedRef
  ) => {
    const sheetRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>

    const setSheetRef = useCallback(
      (node: HTMLDivElement | null) => {
        sheetRef.current = node
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else if (forwardedRef) {
          ;(forwardedRef as MutableRefObject<HTMLDivElement | null>).current = node
        }
      },
      [forwardedRef]
    )

    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (disableOutsideClick) return
        if (onBackdropClick) {
          onBackdropClick(event)
          return
        }
        setIsOpen(false)
      },
      [disableOutsideClick, onBackdropClick, setIsOpen]
    )

    useClickOutside(sheetRef, handleClickOutside, {
      enabled: isOpen && !disableOutsideClick,
      shouldIgnoreTarget: shouldIgnoreOutsideClickTarget
    })

    const { positionClasses, sizeStyles, positionPrefix, slide } = useMemo(() => {
      const isHorizontal = direction === 'left' || direction === 'right'

      const positionMap: Record<SheetDirection, string> = {
        bottom: 'bottom-0 left-0 right-0',
        top: 'top-0 left-0 right-0',
        left: 'left-0 top-0 bottom-0',
        right: 'right-0 top-0 bottom-0'
      }

      const toPx = (v: number | string) => (typeof v === 'number' ? `${v}px` : v)

      // Legacy `coverage` maps to a viewport percentage along the slide axis.
      const axisSize = isHorizontal ? width : height
      const resolved =
        axisSize !== undefined
          ? toPx(axisSize)
          : coverage !== undefined
            ? `${coverage}${isHorizontal ? 'vw' : 'vh'}`
            : isHorizontal
              ? '100%'
              : '90vh'

      const sizeStyles = isHorizontal ? { width: resolved, height: '100%' } : { height: resolved, width: '100%' }

      return {
        positionClasses: positionMap[direction],
        sizeStyles,
        positionPrefix: withinContainer ? 'absolute' : 'fixed',
        slide: slideOffsets(direction)
      }
    }, [direction, width, height, coverage, withinContainer])

    const backdropInteractive = !onBackdropClick && !hideOverlay

    return (
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key='generic-slide-backdrop'
              aria-hidden
              className={`${positionPrefix} inset-0 z-40 bg-black !mt-0 ${backdropInteractive ? 'pointer-events-auto' : 'pointer-events-none'}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: hideOverlay ? 0 : 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: BACKDROP_FADE_DURATION, ease: BACKDROP_FADE_EASE }}
            />

            <motion.div
              key='generic-slide-sheet'
              ref={setSheetRef}
              className={`${positionPrefix} flex flex-col z-50 overflow-y-auto bg-white shadow-xl scrollbar-hide pointer-events-auto ${positionClasses} ${className}`}
              style={{ ...sizeStyles, willChange: 'transform' }}
              initial={slide.initial}
              animate={{ x: 0, y: 0 }}
              exit={slide.exit}
              transition={{ type: 'spring', duration: 0.5, bounce: 0.2 }}
              onAnimationComplete={definition => {
                if (
                  typeof definition === 'object' &&
                  definition !== null &&
                  'x' in definition &&
                  'y' in definition &&
                  definition.x === 0 &&
                  definition.y === 0
                ) {
                  onEnterComplete?.()
                }
              }}
            >
              <div className='flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col'>{children}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    )
  }
)

GenericSlideInDialog.displayName = 'GenericSlideInDialog'

export default GenericSlideInDialog
